import { useState, useEffect, useMemo, useRef, ReactNode } from "react";
import { useStockControl } from "@/hooks/useStockControl";
import { useCompanerosVisibles, useLocationScope } from "@/hooks/useLocationScope";
import { formatStock } from "@/lib/formatStock";
import { useTranslation } from "react-i18next";
import { getTaskTypeIcon, taskTypeTileShadow } from "@/lib/taskTypeIcon";
import { PILL_COLORS } from "@/lib/statusPill";
import { PersonAvatar } from "@/components/ui/PersonAvatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  MapPin,
  Bug,
  FlaskConical,
  User,
  AlertTriangle,
  Package,
  Loader2,
  Home,
  Layers,
  FileText,
  Tag,
  Calendar as CalendarIcon,
  Clock,
  Flag,
  Pencil,
  Check as CheckIcon,
  X as XIcon,
  LucideIcon,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePestTranslation } from "@/hooks/usePestTranslation";
import { cn } from "@/lib/utils";
import { resolveProductAmount, resolveCaldoLiters, classifyUnit } from "@/lib/treatmentDosing";
import { resolveCustomAmount, buildCustomProductsFromParsed, doseUnitForProduct, type ParsedProduct } from "@/lib/voiceCustomProducts";
import TreatmentProductsSection, { type TreatmentProduct } from "@/components/planner/TreatmentProductsSection";
import { Button } from "@/components/ui/button";
import { buildAutoTaskTitle } from "@/lib/taskTitle";
import { cardStructureDefault } from "@/lib/voiceCardDefaults";
import { touch, productRateKey, productUnitKey } from "@/lib/voiceOutcome";

export interface ResolvedProduct {
  product_id: string;
  product_name: string;
  amount: number;
  unit: string;
  stock?: number;
  is_from_treatment?: boolean;
  amount_per_ha?: number | null;
  rate_value?: number | null;
  rate_unit?: string | null;
  stock_unit?: string | null;
  dose_value?: number | null;
  dose_unit?: string | null;
  target_blocks?: string[];
  /** Concentration/variant label from inventory (e.g. "80%"). */
  variant_label?: string | null;
  units_per_package?: number | null;
  /** La dosis vino de la costumbre (DV16) y no se ha tocado: la tarjeta lo avisa. */
  dose_source?: string | null;
}

export interface VoiceTaskLocationWarning {
  type:
    | "location_conflict"
    | "hierarchy_mismatch"
    | "location_corrected"
    | "greenhouse_not_in_location"
    /** Se dictó una finca/invernadero que no está en el alcance de quien dicta. */
    | "out_of_scope";
  spoken_location?: string | null;
  resolved_location?: string | null;
  attempted_greenhouse?: string | null;
  real_location_of_greenhouse?: string | null;
  structure_name?: string | null;
  dropped_blocks?: string[];
}

export interface VoiceTaskData {
  title: string;
  /** Client-only: true once the user manually edits the title. While false, title auto-regenerates from selected structure. */
  title_customized?: boolean;
  type: string;
  priority: string;
  location_id?: string | null;
  location_name?: string | null;
  greenhouse_id?: string | null;
  greenhouse_name?: string | null;
  block_ids?: string[];
  block_names?: string[];
  observed_rows?: Record<string, { row_start: number | null; row_end: number | null }> | null;
  pest_id?: string | null;
  pest_name?: string | null;
  pest_translation_key?: string | null;
  /** "configured" = a pre-configured treatment recipe; "custom" = ad-hoc product list. */
  treatment_kind?: "configured" | "custom";
  treatment_id?: string | null;
  treatment_name?: string | null;
  /** Voice-parsed product list (custom mode), before/awaiting resolution into resolved_products. */
  parsed_products?: ParsedProduct[];
  /** Custom-mode products the user still has to disambiguate (spoken name matched 0 or >1 inventory rows). */
  unresolved_products?: ParsedProduct[];
  assigned_to?: string | null;
  worker_name?: string | null;
  scheduled_date: string;
  scheduled_time?: string;
  notes?: string | null;
  resolved_products?: ResolvedProduct[];
  /** Total area (m²) of the selected blocks at enrichment time (for caldo re-resolve). */
  resolved_area_m2?: number | null;
  /** Caldo (spray-mix) volume for caldo-based recipes. */
  water_volume?: number | null;
  water_volume_unit?: string | null;
  location_warning?: VoiceTaskLocationWarning | null;
  /**
   * Client-only (final_result, anexo B §3.3): campos que la PERSONA tocó en esta
   * tarjeta. Lo anotan los editores de abajo; los efectos automáticos no.
   */
  user_touched?: string[];
  /** Client-only: lo que la tarjeta rellenó sola y con qué valor (voiceCardDefaults). */
  card_defaults?: Record<string, unknown>;
  /** Defaults aprendidos que trae el motor (habits.ts): campo → origen, n y cuota. */
  autofill?: Record<string, { source?: string | null; n?: number | null; share?: number | null } | null | undefined>;
  /** Origen de cada valor según el motor (VoiceTaskOut.sources). No se pinta: va a final_result. */
  sources?: Record<string, string | null | undefined> | null;
}

interface Props {
  taskData: VoiceTaskData;
  taskIndex?: number;
  totalTasks?: number;
  onConfirm: (data: VoiceTaskData) => void;
  onRetry: () => void;
  onTaskDataChange: (data: VoiceTaskData) => void;
  companyId: string;
}

interface TreatmentOption {
  id: string;
  name: string;
  /** «biological» | «chemical» — decide el color de la baldosa del tratamiento. */
  mode?: string | null;
}

interface WorkerOption {
  user_id: string;
  full_name: string;
  avatar_url?: string | null;
  role?: string;
}

/* -----------------------------------------------------------------
 * Inline-editable row
 * ----------------------------------------------------------------- */
type FieldKey =
  | "title"
  | "type"
  | "date"
  | "time"
  | "location"
  | "greenhouse"
  | "block"
  | "treatment"
  | "worker"
  | "priority";

interface RowProps {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  editable?: boolean;
  isEditing?: boolean;
  onEditOpen?: () => void;
  onEditClose?: () => void;
  editor?: ReactNode;
  warning?: boolean;
  /** Color sólido de la baldosa. Sin él se usa el gris neutro. */
  tone?: string;
  /** Para la fila del trabajador: en vez del icono, su foto redonda. */
  avatar?: { name?: string | null; url?: string | null };
}

/** Gris neutro para las filas sin color propio. */
const TONE_NEUTRAL = "#64748B";

function Row({
  icon: Icon, label, value, editable = true, isEditing, onEditOpen, onEditClose, editor, warning, tone, avatar,
}: RowProps) {
  const { t } = useTranslation();
  // La baldosa va a color completo con el icono en blanco, como en el resto de la app.
  // Antes era un azul muy claro con el icono en negro y todas las filas iguales, así que
  // no se distinguía de un vistazo qué era cada cosa.
  const tileColor = warning ? "#E0962E" : (tone || TONE_NEUTRAL);
  return (
    <div className="flex items-start gap-3 px-3 py-3">
      {avatar && !warning ? (
        // Con `warning` no hay nadie elegido, así que la foto sobra: manda la baldosa
        // ámbar, que es la que avisa de que falta rellenar la fila.
        <PersonAvatar name={avatar.name} avatarUrl={avatar.url} size={36} className="mt-0.5" />
      ) : (
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
          style={{ backgroundColor: tileColor, boxShadow: taskTypeTileShadow(tileColor) }}
        >
          <Icon className="w-4 h-4 text-white" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">{label}</p>
        {isEditing ? (
          <div className="mt-1.5">{editor}</div>
        ) : (
          <div className={cn("text-sm mt-0.5 break-words", warning ? "text-amber-700" : "text-slate-900")}>
            {value || <span className="text-slate-400 italic">—</span>}
          </div>
        )}
      </div>
      {editable && (
        <div className="shrink-0 flex items-center gap-1">
          {isEditing ? (
            <button
              type="button"
              onClick={onEditClose}
              aria-label={t("voiceModal.actions.done")}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onEditOpen}
              aria-label={t("voiceModal.actions.edit")}
              className="w-8 h-8 rounded-full flex items-center justify-center text-black hover:text-black hover:bg-slate-50 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* -----------------------------------------------------------------
 * Main component
 * ----------------------------------------------------------------- */
export function VoiceTaskConfirmation({ taskData, taskIndex, totalTasks, onTaskDataChange, companyId }: Props) {
  const { t } = useTranslation();
  const { getName: getPestName } = usePestTranslation();
  const { flexible: stockFlexible } = useStockControl();

  const [treatmentOptions, setTreatmentOptions] = useState<TreatmentOption[]>([]);
  const [workerOptions, setWorkerOptions] = useState<WorkerOption[]>([]);
  const [loadingTreatments, setLoadingTreatments] = useState(false);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [locationWarning, setLocationWarning] = useState<Array<{ product_name: string; available_locations: string[] }> | null>(null);
  /* La estructura ENTERA de la empresa, sin recortar. Los desplegables se
     recortan más abajo, pero para AVISAR de que lo dictado cae fuera hace falta
     poder reconocerlo: una finca que no está en la lista recortada puede ser
     «no es suya» o «todavía no ha cargado», y son cosas muy distintas. Con la
     lista completa se distinguen. Leerlas es seguro: la RLS de `locations`,
     `greenhouses` y `blocks` es de empresa, no de finca. */
  const [todasLasFincas, setTodasLasFincas] = useState<{ id: string; name: string }[]>([]);
  const [todosLosInvernaderos, setTodosLosInvernaderos] = useState<{ id: string; name: string; location_id: string | null }[]>([]);
  const [todosLosSectores, setTodosLosSectores] = useState<{ id: string; name: string; greenhouse_id: string }[]>([]);
  const [hierarchyMismatch, setHierarchyMismatch] = useState<string | null>(null);
  const [inventoryProducts, setInventoryProducts] = useState<{ id: string; product_name: string; unit: string; quantity: number; variant_label: string | null; units_per_package: number | null }[]>([]);
  /* El inventario tarda en llegar. Sin este flag no se distingue «todavía no ha
     cargado» de «esta empresa no tiene productos», y el aviso de inventario vacío
     parpadearía en cada apertura. */
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [isCustomizing, setIsCustomizing] = useState(true);
  /** Invernadero cuyos sectores ya se rellenaron solos (ver el efecto de abajo). */
  const autorrellenado = useRef<string | null>(null);

  // Only one field open at a time
  const [editingField, setEditingField] = useState<FieldKey | null>(null);
  const { allowedLocationIds, excludedBlockIds, loading: alcanceCargando } = useLocationScope();
  /* Y LA TAREA POR VOZ IGUAL (24-09-2026): las fincas y los sectores de este
     mismo diálogo ya iban recortados y el selector de persona no, que era la
     incoherencia más visible de las tres. Quien ya está asignado no se cae
     nunca: al rectificar una tarea dictada, quitarlo lo borraría al guardar. */
  const { esVisible: companeroVisible } = useCompanerosVisibles();

  /* Se recorta lo que se OFRECE, no `workerOptions`: de esa lista salen
     también el nombre y la foto del asignado (líneas de abajo), y un nombre
     que no se resuelve deja la tarjeta en blanco. */
  const opcionesVisibles = useMemo(
    () => workerOptions.filter((w) => companeroVisible(w.user_id) || w.user_id === taskData.assigned_to),
    [workerOptions, companeroVisible, taskData.assigned_to],
  );

  /* LOS TRES DESPLEGABLES, SOLO CON LO SUYO (02-09-2026).
     Aquí no se está mirando nada: se está CREANDO una tarea. Que el
     desplegable de localización ofreciera Campohermoso a un encargado de Las
     Norias no era enseñar de más, era dejarle mandar trabajo a una finca que no
     lleva — y el dictado, que resuelve la finca de lo que se ha dicho en voz
     alta, podía plantarla ahí él solo sin que nadie tocara el desplegable.
     Esto es la comodidad; quien lo impide de verdad es la regla de la base de
     datos (`greenhouse_in_member_scope`, en la política de INSERT), porque hay
     cuatro caminos para crear una tarea y este es solo uno. */
  useEffect(() => {
    if (!companyId) return;
    supabase
      .from("locations")
      .select("id, name")
      .eq("company_id", companyId)
      .order("name")
      .then(({ data }) => setTodasLasFincas(data || []));
  }, [companyId]);

  // Fetch greenhouses
  useEffect(() => {
    if (!companyId) return;
    supabase
      .from("greenhouses")
      .select("id, name, location_id")
      .eq("company_id", companyId)
      .order("name")
      .then(({ data }) => setTodosLosInvernaderos(data || []));
  }, [companyId]);

  // Fetch blocks
  useEffect(() => {
    if (!companyId) return;
    supabase
      .from("blocks")
      .select("id, name, greenhouse_id")
      .eq("company_id", companyId)
      .is("archived_at", null)
      .order("name")
      .then(({ data }) => setTodosLosSectores(data || []));
  }, [companyId]);

  /* LOS DESPLEGABLES, SOLO CON LO SUYO. El recorte se aplica aquí y no en la
     consulta para que arriba quede la lista completa (ver el porqué allí). */
  const locationOptions = useMemo(
    () => (allowedLocationIds ? todasLasFincas.filter((l) => allowedLocationIds.has(l.id)) : todasLasFincas),
    [todasLasFincas, allowedLocationIds],
  );
  const greenhouseOptions = useMemo(
    () =>
      allowedLocationIds
        ? todosLosInvernaderos.filter((g) => g.location_id && allowedLocationIds.has(g.location_id))
        : todosLosInvernaderos,
    [todosLosInvernaderos, allowedLocationIds],
  );
  const blockOptions = useMemo(
    () => (excludedBlockIds?.size ? todosLosSectores.filter((b) => !excludedBlockIds.has(b.id)) : todosLosSectores),
    [todosLosSectores, excludedBlockIds],
  );

  /* LO QUE SE DICTA FUERA DE SU ALCANCE SE BORRA Y SE DICE (02-09-2026).
     Ron, encargado de Las Norias, dictó «…en Campohermoso» y la pantalla se
     quedó con la finca puesta por dentro y el desplegable EN BLANCO por fuera:
     Campohermoso no está entre sus opciones, así que el `Select` no tenía nada
     que pintar. No podía crear la tarea —la política de INSERT no se lo
     habría permitido— pero tampoco entendía por qué, y el campo vacío parecía
     un fallo de la aplicación.
     Un valor que la pantalla no puede enseñar no puede quedarse guardado. Se
     quita y se avisa con el nombre de lo que se dijo, que es lo único que
     explica el hueco. Ojo con dos cosas: mientras el alcance no ha contestado
     (`loading`) no se toca nada, porque en ese rato «sus fincas» es el conjunto
     vacío y se borraría todo; y si la lista completa aún no ha llegado tampoco,
     porque «no está en la lista» significaría «no ha cargado». */
  useEffect(() => {
    if (!allowedLocationIds || alcanceCargando) return;

    const finca = taskData.location_id;
    const fincaFuera = !!finca && todasLasFincas.length > 0 && !allowedLocationIds.has(finca);

    const inv = taskData.greenhouse_id
      ? todosLosInvernaderos.find((g) => g.id === taskData.greenhouse_id)
      : undefined;
    /* Un invernadero sin finca no es de nadie y se queda, igual que en el resto
       del recorte: esconderlo sería quitarle trabajo a quien sí lo tiene. */
    const invFuera = !!inv?.location_id && !allowedLocationIds.has(inv.location_id);

    const sectores = taskData.block_ids || [];
    const sectoresFuera = sectores.filter(
      (b) =>
        excludedBlockIds?.has(b) ||
        (todosLosSectores.length > 0 &&
          (() => {
            const s = todosLosSectores.find((x) => x.id === b);
            const g = s && todosLosInvernaderos.find((x) => x.id === s.greenhouse_id);
            return !!g?.location_id && !allowedLocationIds.has(g.location_id);
          })()),
    );

    if (!fincaFuera && !invFuera && sectoresFuera.length === 0) return;

    const dicho =
      (fincaFuera ? taskData.location_name || todasLasFincas.find((l) => l.id === finca)?.name : null) ||
      (invFuera ? taskData.greenhouse_name || inv?.name : null) ||
      taskData.location_name ||
      taskData.greenhouse_name ||
      "—";

    onTaskDataChange({
      ...taskData,
      location_id: fincaFuera ? null : taskData.location_id,
      location_name: fincaFuera ? null : taskData.location_name,
      greenhouse_id: fincaFuera || invFuera ? null : taskData.greenhouse_id,
      greenhouse_name: fincaFuera || invFuera ? null : taskData.greenhouse_name,
      block_ids: fincaFuera || invFuera ? [] : sectores.filter((b) => !sectoresFuera.includes(b)),
      block_names:
        fincaFuera || invFuera
          ? []
          : (taskData.block_names || []).filter((_, i) => !sectoresFuera.includes(sectores[i])),
      observed_rows: fincaFuera || invFuera ? null : taskData.observed_rows,
      location_warning: { type: "out_of_scope", spoken_location: dicho },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowedLocationIds,
    excludedBlockIds,
    alcanceCargando,
    todasLasFincas,
    todosLosInvernaderos,
    todosLosSectores,
    taskData.location_id,
    taskData.greenhouse_id,
    JSON.stringify(taskData.block_ids),
  ]);

  // Fetch inventory (products) — needed for the custom-treatment product builder.
  useEffect(() => {
    if (!companyId) return;
    supabase
      .from("inventory")
      .select("id, product_name, unit, quantity, variant_label, units_per_package")
      .eq("company_id", companyId)
      .is("archived_at", null)
      .order("product_name")
      .then(({ data }) => {
        setInventoryProducts((data as any) || []);
        setInventoryLoaded(true);
      });
  }, [companyId]);

  // Keep greenhouse ↔ section consistent so the mandatory-greenhouse gate never forces a
  // re-pick that would wipe a spoken section subset:
  //  • sections parsed but no greenhouse ⇒ derive the greenhouse from them;
  //  • greenhouse set with no section ⇒ cover the whole greenhouse (all sections).
  //  La regla vive en `cardStructureDefault` (src/lib/voiceCardDefaults.ts): la
  //  misma que usa el corpus de voz para medir lo que ve el usuario, y que anota
  //  en `card_defaults` lo que puso para saber luego si se aceptó.
  /* UNA VEZ POR INVERNADERO, y no cada vez que la lista se quede vacía. Este
     efecto existe para que elegir invernadero cubra sus sectores solo; si
     además reacciona a CUALQUIER lista vacía, deshace el vaciado del usuario
     en el mismo render y «Todas las secciones» parece un botón muerto. */
  useEffect(() => {
    const r = cardStructureDefault(taskData, { blockOptions, greenhouseOptions, locationOptions }, autorrellenado.current);
    if (!r) return;
    autorrellenado.current = r.autofilledGreenhouseId;
    onTaskDataChange(r.task);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskData.greenhouse_id, JSON.stringify(taskData.block_ids), blockOptions, greenhouseOptions]);

  // Hierarchy consistency check
  useEffect(() => {
    if (!companyId) { setHierarchyMismatch(null); return; }
    let cancelled = false;
    (async () => {
      try {
        if (taskData.greenhouse_id && taskData.location_id) {
          const { data: gh } = await supabase
            .from("greenhouses")
            .select("location_id")
            .eq("id", taskData.greenhouse_id)
            .maybeSingle();
          if (gh && gh.location_id && gh.location_id !== taskData.location_id) {
            if (!cancelled) setHierarchyMismatch(t("voiceTask.hierarchyMismatchWarning"));
            return;
          }
        }
        if (taskData.greenhouse_id && taskData.block_ids && taskData.block_ids.length > 0) {
          const { data: bs } = await supabase
            .from("blocks")
            .select("id, greenhouse_id")
            .in("id", taskData.block_ids);
          const bad = (bs || []).find((b) => b.greenhouse_id !== taskData.greenhouse_id);
          if (bad) {
            if (!cancelled) setHierarchyMismatch(t("voiceTask.hierarchyMismatchWarning"));
            return;
          }
        }
        if (!cancelled) setHierarchyMismatch(null);
      } catch {
        if (!cancelled) setHierarchyMismatch(null);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, taskData.greenhouse_id, taskData.location_id, JSON.stringify(taskData.block_ids), t]);

  const handleLocationSelect = (locId: string) => {
    const loc = locationOptions.find(l => l.id === locId);
    onTaskDataChange({
      ...touch(taskData, "location_id"),
      location_id: locId,
      location_name: loc?.name || null,
      greenhouse_id: null,
      greenhouse_name: null,
      block_ids: [],
      block_names: [],
      observed_rows: null,
      location_warning: null,
      // Keep a custom (ad-hoc) product list across structure edits; a configured recipe re-enriches.
      resolved_products: taskData.treatment_kind === "custom" ? taskData.resolved_products : undefined,
    });
  };

  const handleGreenhouseSelect = (ghId: string) => {
    const gh = greenhouseOptions.find(g => g.id === ghId);
    const ghBlocks = blockOptions.filter(b => b.greenhouse_id === ghId);
    // Preserve an existing section subset if it already belongs to this greenhouse (so we
    // never silently widen a spoken "sección 4 y 5" to the whole greenhouse); otherwise
    // default to the WHOLE greenhouse (every section). The user can narrow afterwards.
    const keptIds = (taskData.block_ids || []).filter(id => ghBlocks.some(b => b.id === id));
    const useIds = keptIds.length > 0 ? keptIds : ghBlocks.map(b => b.id);
    const nameById = new Map(ghBlocks.map(b => [b.id, b.name]));
    onTaskDataChange({
      ...touch(taskData, "greenhouse_id"),
      greenhouse_id: ghId,
      greenhouse_name: gh?.name || null,
      location_id: taskData.location_id || gh?.location_id || null,
      location_name:
        taskData.location_name ||
        (gh?.location_id ? (locationOptions.find(l => l.id === gh.location_id)?.name || null) : null),
      block_ids: useIds,
      block_names: useIds.map(id => nameById.get(id)!).filter(Boolean),
      observed_rows: null,
      location_warning: null,
      // Keep a custom (ad-hoc) product list across structure edits; a configured recipe re-enriches.
      resolved_products: taskData.treatment_kind === "custom" ? taskData.resolved_products : undefined,
    });
  };

  /** Select the whole greenhouse (all its sections). */
  /**
   * «Todas las secciones» ES UN INTERRUPTOR (02-09-2026).
   *
   * Antes solo sabía marcar. Con ocho sectores y dos que interesan, había que
   * desmarcar seis de uno en uno; y estando ya todos marcados, pulsarlo no hacía
   * absolutamente nada, que es lo que más desconcierta de un botón.
   *
   * Vaciar la lista significa «el invernadero entero, sin desglose» —lo mismo
   * que un parte con `block_id` nulo—, y en un tratamiento no se queda a medias:
   * sin sectores no hay superficie, así que el caldo no resuelve y `Confirmar`
   * sigue bloqueado hasta elegir alguno.
   */
  const handleSelectAllSections = () => {
    if (!taskData.greenhouse_id) return;
    const ghBlocks = blockOptions.filter(b => b.greenhouse_id === taskData.greenhouse_id);
    const todosPuestos = ghBlocks.length > 0 && ghBlocks.every(b => (taskData.block_ids || []).includes(b.id));
    onTaskDataChange({
      ...touch(taskData, "block_ids"),
      block_ids: todosPuestos ? [] : ghBlocks.map(b => b.id),
      block_names: todosPuestos ? [] : ghBlocks.map(b => b.name),
      resolved_products: taskData.treatment_kind === "custom" ? taskData.resolved_products : undefined,
    });
  };

  /** Toggle a single section on/off, keeping at least one selected (whole-greenhouse never empties). */
  const toggleSection = (blockId: string) => {
    const cur = taskData.block_ids || [];
    const isSel = cur.includes(blockId);
    if (isSel && cur.length <= 1) return; // must keep at least one section
    const nextSet = new Set(cur);
    if (isSel) nextSet.delete(blockId); else nextSet.add(blockId);
    // Keep the section order stable (greenhouse block order).
    const ordered = blockOptions.filter(b => b.greenhouse_id === taskData.greenhouse_id && nextSet.has(b.id));
    onTaskDataChange({
      ...touch(taskData, "block_ids"),
      block_ids: ordered.map(b => b.id),
      block_names: ordered.map(b => b.name),
      resolved_products: taskData.treatment_kind === "custom" ? taskData.resolved_products : undefined,
    });
  };

  // Fetch treatment options
  useEffect(() => {
    if (taskData.type !== "treatment" || !companyId) return;
    setLoadingTreatments(true);

    const fetchTreatments = async () => {
      try {
        if (taskData.pest_id) {
          const { data: mappings } = await supabase
            .from("treatment_pest_map")
            .select("treatment_id")
            .eq("pest_id", taskData.pest_id);

          const treatmentIds = (mappings || []).map(m => m.treatment_id);

          if (treatmentIds.length > 0) {
            const { data: treatments } = await supabase
              .from("treatments")
              .select("id, name, mode")
              .in("id", treatmentIds)
              .eq("company_id", companyId);
            setTreatmentOptions(treatments || []);
          } else {
            const { data: treatments } = await supabase
              .from("treatments")
              .select("id, name, mode")
              .eq("company_id", companyId);
            setTreatmentOptions(treatments || []);
          }
        } else {
          const { data: treatments } = await supabase
            .from("treatments")
            .select("id, name")
            .eq("company_id", companyId);
          setTreatmentOptions(treatments || []);
        }
      } catch (err) {
        console.error("Error fetching treatments:", err);
      } finally {
        setLoadingTreatments(false);
      }
    };

    fetchTreatments();
  }, [taskData.type, companyId, taskData.pest_id]);

  // Fetch worker options — con rol, para dejar fuera a los admins (antes salía
  // literalmente todo perfil de la empresa en el desplegable).
  useEffect(() => {
    if (!companyId) return;
    setLoadingWorkers(true);

    Promise.all([
      supabase.from("profiles").select("user_id, full_name, avatar_url").eq("company_id", companyId),
      supabase.from("user_roles").select("user_id, role").eq("company_id", companyId),
    ]).then(([{ data: profiles }, { data: roles }]) => {
      const roleMap = new Map<string, string>();
      (roles || []).forEach((r) => roleMap.set(r.user_id, r.role));
      const options = (profiles || [])
        .filter((p) => ["worker", "manager", "perito"].includes(roleMap.get(p.user_id) || ""))
        .map((p) => ({ ...p, role: roleMap.get(p.user_id) }));
      setWorkerOptions(options);
      setLoadingWorkers(false);
    });
  }, [companyId]);

  // Asignados a los sectores dictados: van primero en el selector de trabajador.
  const [blockAssignedIds, setBlockAssignedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const ids = taskData.block_ids || [];
    if (!companyId || ids.length === 0) {
      setBlockAssignedIds(new Set());
      return;
    }
    supabase
      .from("block_worker_assignments")
      .select("user_id")
      .eq("company_id", companyId)
      .eq("is_active", true)
      .in("block_id", ids)
      .then(({ data }) => setBlockAssignedIds(new Set((data || []).map((d) => d.user_id))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, JSON.stringify(taskData.block_ids)]);

  // Validate treatment products against greenhouse location stock
  useEffect(() => {
    if (!taskData.treatment_id || !taskData.greenhouse_id || !companyId) {
      setLocationWarning(null);
      return;
    }

    const checkLocationStock = async () => {
      try {
        const { data: gh } = await supabase
          .from("greenhouses")
          .select("location_id")
          .eq("id", taskData.greenhouse_id!)
          .single();

        if (!gh?.location_id) { setLocationWarning(null); return; }

        const { data: components } = await supabase
          .from("treatment_components")
          .select("product_id, inventory!inner(product_name)")
          .eq("treatment_id", taskData.treatment_id!)
          .eq("company_id", companyId);

        if (!components || components.length === 0) { setLocationWarning(null); return; }

        const { data: stockRows } = await supabase
          .from("inventory_location_stock")
          .select("product_id")
          .eq("location_id", gh.location_id)
          .eq("company_id", companyId)
          .gt("quantity", 0);

        const { data: allAssigned } = await supabase
          .from("inventory_location_stock")
          .select("product_id")
          .eq("company_id", companyId);

        const locationSet = new Set((stockRows || []).map(r => r.product_id));
        const assignedSet = new Set((allAssigned || []).map(r => r.product_id));

        const missing = components.filter((c: any) => assignedSet.has(c.product_id) && !locationSet.has(c.product_id));

        if (missing.length === 0) { setLocationWarning(null); return; }

        const missingIds = missing.map((c: any) => c.product_id);
        const { data: altStock } = await supabase
          .from("inventory_location_stock")
          .select("product_id, location_id, quantity")
          .in("product_id", missingIds)
          .gt("quantity", 0);

        const locIds = [...new Set((altStock || []).map(r => r.location_id))];
        let locsMap: Record<string, string> = {};
        if (locIds.length > 0) {
          const { data: locs } = await supabase.from("locations").select("id, name").in("id", locIds);
          locsMap = Object.fromEntries((locs || []).map(l => [l.id, l.name]));
        }

        setLocationWarning(missing.map((c: any) => {
          const rows = (altStock || []).filter(r => r.product_id === c.product_id);
          return {
            product_name: (c as any).inventory?.product_name || "?",
            available_locations: rows.map(r => locsMap[r.location_id]).filter(Boolean),
          };
        }));
      } catch (err) {
        console.error("Error checking voice task location stock:", err);
        setLocationWarning(null);
      }
    };

    checkLocationStock();
  }, [taskData.treatment_id, taskData.greenhouse_id, companyId]);

  /** Clave de final_result de cada campo que edita `update` (anexo B §3.3). */
  const TOUCH_KEY: Partial<Record<keyof VoiceTaskData, string>> = {
    type: "type",
    scheduled_date: "date",
    scheduled_time: "time",
    priority: "priority",
  };

  const update = (field: keyof VoiceTaskData, value: string) => {
    onTaskDataChange({ ...touch(taskData, TOUCH_KEY[field] ?? String(field)), [field]: value });
  };

  const handleTitleChange = (value: string) => {
    onTaskDataChange({ ...touch(taskData, "title"), title: value, title_customized: true });
  };

  const handleTreatmentSelect = (treatmentId: string) => {
    const selected = treatmentOptions.find(tr => tr.id === treatmentId);
    onTaskDataChange({
      ...touch(taskData, "treatment_id"),
      treatment_id: treatmentId,
      treatment_name: selected?.name || null,
      resolved_products: undefined,
    });
  };

  const handleWorkerSelect = (userId: string) => {
    const selected = workerOptions.find(w => w.user_id === userId);
    onTaskDataChange({
      ...touch(taskData, "assigned_to"),
      assigned_to: userId,
      worker_name: selected?.full_name || null,
    });
  };

  /* ---------- custom (ad-hoc) treatment handlers ---------- */
  const customAreaM2 = taskData.resolved_area_m2 || 0;
  const caldoFor = (wv?: number | null, wu?: string | null) =>
    resolveCaldoLiters(wv ?? taskData.water_volume ?? null, wu ?? taskData.water_volume_unit ?? "L", customAreaM2);
  const caldoL = caldoFor();
  const caldoMissing =
    (taskData.resolved_products || []).some((p) => classifyUnit(p.rate_unit || p.unit) === "per_caldo") &&
    !(caldoL != null && caldoL > 0);

  const recomputeProduct = (p: ResolvedProduct, cL: number | null): ResolvedProduct => {
    const rate_value = p.rate_value ?? 0;
    const rate_unit = p.rate_unit || p.unit;
    const stock_unit = p.stock_unit || p.unit;
    const calc = resolveCustomAmount(rate_value, rate_unit, stock_unit, customAreaM2, cL);
    return { ...p, rate_value, rate_unit, stock_unit, amount: calc.amount, unit: calc.unit, dose_value: calc.dose_value, dose_unit: calc.dose_unit, amount_per_ha: calc.amount_per_ha };
  };

  const setTreatmentKind = (kind: "configured" | "custom") => {
    if (kind === "custom") {
      // Keep any manually/voice-resolved custom products; drop recipe-derived ones.
      let resolved_products = (taskData.resolved_products || []).filter((p) => !p.is_from_treatment);
      let unresolved_products = taskData.unresolved_products || [];
      // If nothing survives (e.g. after a configured round-trip) but we still have the
      // voice parse, re-derive the custom list from it instead of silently losing it.
      if (resolved_products.length === 0 && unresolved_products.length === 0 && (taskData.parsed_products?.length ?? 0) > 0) {
        const invById = new Map(inventoryProducts.map((p) => [p.id, p]));
        const built = buildCustomProductsFromParsed(taskData.parsed_products!, invById, taskData.block_ids || [], customAreaM2, caldoL);
        resolved_products = built.resolved;
        unresolved_products = built.unresolved;
      }
      onTaskDataChange({
        ...touch(taskData, "treatment_kind"),
        treatment_kind: "custom",
        treatment_id: null,
        treatment_name: null,
        // A custom (ad-hoc) treatment has no pest — mirror the edge function's invariant.
        pest_id: null,
        pest_name: null,
        pest_translation_key: null,
        resolved_products,
        unresolved_products,
      });
      setIsCustomizing(true);
    } else {
      onTaskDataChange({
        ...touch(taskData, "treatment_kind"),
        treatment_kind: "configured",
        resolved_products: undefined,
        unresolved_products: [],
        // Clear any custom water volume so the picked recipe uses its own default caldo.
        water_volume: null,
        water_volume_unit: null,
      });
    }
  };

  const newCustomProduct = (inv: { id: string; product_name: string; unit: string; quantity: number; variant_label: string | null; units_per_package: number | null }, rate_value: number, rate_unit: string): ResolvedProduct => {
    const stock_unit = inv.unit || "L";
    return recomputeProduct({
      product_id: inv.id,
      product_name: inv.product_name,
      variant_label: inv.variant_label ?? null,
      amount: 0,
      unit: stock_unit,
      stock: inv.quantity ?? 0,
      is_from_treatment: false,
      rate_value,
      rate_unit: rate_unit || stock_unit,
      stock_unit,
      units_per_package: inv.units_per_package ?? null,
      target_blocks: taskData.block_ids || [],
    }, caldoFor());
  };

  const handleAddCustomProduct = (productId: string) => {
    const inv = inventoryProducts.find((p) => p.id === productId);
    if (!inv || (taskData.resolved_products || []).some((p) => p.product_id === productId)) return;
    onTaskDataChange({ ...touch(taskData, "products"), resolved_products: [...(taskData.resolved_products || []), newCustomProduct(inv, 0, inv.unit || "L")] });
  };

  const handleCustomRateValueChange = (index: number, value: number) => {
    const cL = caldoFor();
    const pid = (taskData.resolved_products || [])[index]?.product_id;
    // Tocada la dosis, ya es suya: fuera el aviso de «habitual».
    onTaskDataChange({ ...touch(taskData, pid ? productRateKey(pid) : "products"), resolved_products: (taskData.resolved_products || []).map((p, i) => (i === index ? recomputeProduct({ ...p, rate_value: value, dose_source: null }, cL) : p)) });
  };

  const handleCustomRateUnitChange = (index: number, unit: string) => {
    const cL = caldoFor();
    const pid = (taskData.resolved_products || [])[index]?.product_id;
    onTaskDataChange({ ...touch(taskData, pid ? productUnitKey(pid) : "products"), resolved_products: (taskData.resolved_products || []).map((p, i) => (i === index ? recomputeProduct({ ...p, rate_unit: unit, dose_source: null }, cL) : p)) });
  };

  const handleRemoveCustomProduct = (index: number) => {
    onTaskDataChange({ ...touch(taskData, "products"), resolved_products: (taskData.resolved_products || []).filter((_, i) => i !== index) });
  };

  const handleWaterVolumeChange = (value: number | null) => {
    const unit = taskData.water_volume_unit || "L";
    const cL = caldoFor(value, unit);
    onTaskDataChange({ ...touch(taskData, "water"), water_volume: value, water_volume_unit: unit, resolved_products: (taskData.resolved_products || []).map((p) => recomputeProduct(p, cL)) });
  };

  const handleWaterVolumeUnitChange = (unit: string) => {
    const cL = caldoFor(taskData.water_volume, unit);
    onTaskDataChange({ ...touch(taskData, "water"), water_volume_unit: unit, resolved_products: (taskData.resolved_products || []).map((p) => recomputeProduct(p, cL)) });
  };

  const handlePickUnresolved = (unresolvedIndex: number, productId: string) => {
    const entry = (taskData.unresolved_products || [])[unresolvedIndex];
    const inv = inventoryProducts.find((p) => p.id === productId);
    if (!entry || !inv) return;
    // «3 litros» de un producto en kg: la unidad del producto, no la dicha (29-09).
    const rate_unit = doseUnitForProduct(entry.dose_unit, inv.unit || "L") || inv.unit || "L";
    const rate_value = entry.dose_value != null ? entry.dose_value : 0;
    onTaskDataChange({
      ...touch(taskData, "products"),
      resolved_products: [...(taskData.resolved_products || []).filter((p) => p.product_id !== productId), newCustomProduct(inv, rate_value, rate_unit)],
      unresolved_products: (taskData.unresolved_products || []).filter((_, i) => i !== unresolvedIndex),
    });
  };

  /* Un nombre dictado que el inventario no sabe casar («abamectina») dejaba la
     tarea presa: la tarjeta ambar solo se vaciaba eligiendo un producto, y sin
     vaciarla `isVoiceTaskComplete` nunca deja confirmar. Ahora cada linea se
     puede tirar a la basura y seguir. */
  const handleDismissUnresolved = (unresolvedIndex: number) => {
    const entry = (taskData.unresolved_products || [])[unresolvedIndex];
    if (!entry) return;
    /* Sin tocar `parsed_products` el descarte no dura: alternar Configurado ⇄
       Personalizado vuelve a derivar la lista del dictado y lo recien tirado
       reaparece. Se quita de ahi tambien — solo la primera linea equivalente,
       para no llevarse por delante un segundo producto dictado igual. */
    let dropped = false;
    const parsed_products = taskData.parsed_products
      ? taskData.parsed_products.filter((p) => {
          if (dropped) return true;
          const same =
            (p.spoken_name || null) === (entry.spoken_name || null) &&
            (p.product_id || null) === (entry.product_id || null) &&
            (p.dose_value ?? null) === (entry.dose_value ?? null) &&
            (p.dose_unit || null) === (entry.dose_unit || null);
          if (same) { dropped = true; return false; }
          return true;
        })
      : taskData.parsed_products;
    onTaskDataChange({
      ...touch(taskData, "products"),
      parsed_products,
      unresolved_products: (taskData.unresolved_products || []).filter((_, i) => i !== unresolvedIndex),
    });
  };

  const toTreatmentProduct = (p: ResolvedProduct): TreatmentProduct => ({
    product_id: p.product_id,
    product_name: p.variant_label ? `${p.product_name} · ${p.variant_label}` : p.product_name,
    amount: p.amount,
    unit: p.unit,
    stock: p.stock ?? 0,
    is_from_treatment: !!p.is_from_treatment,
    rate_value: p.rate_value ?? 0,
    rate_unit: p.rate_unit || p.unit,
    stock_unit: p.stock_unit || p.unit,
    dose_value: p.dose_value ?? null,
    dose_unit: p.dose_unit ?? null,
    amount_per_ha: p.amount_per_ha ?? null,
    units_per_package: p.units_per_package ?? null,
    // DV16: de dónde salió la dosis que no se dijo, para que se vea y se cambie si hoy es otra.
    rate_hint:
      p.dose_source === "habitual_usuario" ? t("voiceTask.doseHabitUser")
      : p.dose_source === "habitual" ? t("voiceTask.doseHabitCompany")
      : null,
  });

  const isCustom = taskData.type === "treatment" && taskData.treatment_kind === "custom";

  /**
   * EL TÍTULO AUTOMÁTICO: EL TIPO Y, EN UN TRATAMIENTO, LA PLAGA (usuario 14-09-2026).
   *
   * Sigue la regla común de `buildAutoTaskTitle`: «Tratamiento: Mosca blanca»; sin
   * plaga, el tratamiento configurado («Tratamiento: Spintor»); sin ninguno de los
   * dos, «Tratamiento»; los demás tipos, solo el tipo.
   *
   * Ni ubicación (fuera desde el 05-09-2026: repetía la columna de al lado) ni
   * urgencia (fuera desde el 14-09-2026: la prioridad tiene su propio campo, y un
   * título que cambia al tocar la prioridad no dice qué hay que hacer).
   *
   * Esto es lo que se VE aquí; al guardar, `VoiceTaskPanel` vuelve a aplicar la
   * misma regla a cada tarea, también a las que nadie llegó a abrir. Si el
   * usuario escribe el título a mano (`title_customized`), se respeta.
   *
   * Los títulos VIEJOS siguen en la base de datos tal cual (decisión del usuario).
   */
  useEffect(() => {
    if (taskData.title_customized) return;
    if (!taskData.type) return;
    const generated = buildAutoTaskTitle(
      {
        type: taskData.type,
        pestName: taskData.pest_name,
        treatmentName: taskData.treatment_name,
        customTreatment: taskData.treatment_kind === "custom",
      },
      t,
    );
    if (generated !== taskData.title) {
      onTaskDataChange({ ...taskData, title: generated });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskData.type, taskData.pest_name, taskData.treatment_name, taskData.treatment_kind, taskData.title_customized]);

  /* ---------- helpers ---------- */
  const openEdit = (field: FieldKey) => setEditingField(field);
  const closeEdit = () => setEditingField(null);

  const typeLabel = taskData.type ? t(`planner.types.${taskData.type}`, taskData.type) : "";
  const priorityLabel = taskData.priority ? t(`planner.priorities.${taskData.priority}`, taskData.priority) : "";
  const blocksLabel = taskData.block_names?.length ? taskData.block_names.join(", ") : null;

  // Sin el parche de `::-webkit-date-and-time-value` que llevaban los editores de
  // fecha y hora: cómo cae el valor dentro de la caja en el iPhone lo decide ahora
  // una regla global de src/index.css, que además alcanza a los campos de fecha
  // que nunca llevaron parche alguno.
  const triggerCls = "bg-white border-[#e2ecf5] text-slate-800 shadow-sm h-9 text-sm w-full";

  const filteredGreenhouses = taskData.location_id
    ? greenhouseOptions.filter(gh => gh.location_id === taskData.location_id)
    : greenhouseOptions;
  const filteredBlocks = taskData.greenhouse_id
    ? blockOptions.filter(b => b.greenhouse_id === taskData.greenhouse_id)
    : blockOptions;
  const allSectionsSelected = filteredBlocks.length > 0 && filteredBlocks.every(b => (taskData.block_ids || []).includes(b.id));
  const sectionDisplay = allSectionsSelected ? t("voiceTask.allSections") : (blocksLabel || "");

  const treatmentMissing = taskData.type === "treatment" && !isCustom && !taskData.treatment_id;

  // Modo del tratamiento elegido (biológico / químico) y foto del trabajador elegido:
  // los dos solo se usan para pintar la baldosa de su fila.
  const treatmentMode = treatmentOptions.find((o) => o.id === taskData.treatment_id)?.mode ?? null;
  const selectedWorkerAvatar =
    workerOptions.find((w) => w.user_id === taskData.assigned_to)?.avatar_url ?? null;

  return (
    <div className="space-y-3">
      {totalTasks && totalTasks > 1 && (
        <p className="text-xs text-primary font-semibold">
          {`${(taskIndex ?? 0) + 1} / ${totalTasks}`}
        </p>
      )}

      {/* Fuera de su alcance: se dictó una finca que no lleva. El campo se ha
          quedado en blanco a propósito, y esto es lo que lo explica. */}
      {taskData.location_warning?.type === "out_of_scope" && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-xs text-amber-700">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="space-y-1">
            <p className="font-medium text-amber-800">{t("voiceTask.outOfScopeTitle")}</p>
            <p>
              {t("voiceTask.outOfScopeDescription", {
                spokenLocation: taskData.location_warning.spoken_location || "—",
              })}
            </p>
          </div>
        </div>
      )}

      {/* Greenhouse-not-in-location banner */}
      {taskData.location_warning?.type === "greenhouse_not_in_location" && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs text-red-700">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-red-600" />
          <div className="space-y-1">
            <p className="font-medium text-red-700">{t("voiceTask.greenhouseNotInLocationTitle")}</p>
            <p>
              {t("voiceTask.greenhouseNotInLocationDescription", {
                attemptedGreenhouse: taskData.location_warning.attempted_greenhouse || "—",
                resolvedLocation: taskData.location_warning.resolved_location || taskData.location_warning.spoken_location || "—",
              })}
            </p>
          </div>
        </div>
      )}

      {/* Location conflict banner */}
      {taskData.location_warning?.type === "location_conflict" && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs text-red-700">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-red-600" />
          <div className="space-y-1">
            <p className="font-medium text-red-700">{t("voiceTask.locationConflictTitle")}</p>
            <p>
              {t("voiceTask.locationConflictDescription", {
                spokenLocation: taskData.location_warning.spoken_location || taskData.location_warning.resolved_location || "—",
                attemptedGreenhouse: taskData.location_warning.attempted_greenhouse || "—",
                realLocation: taskData.location_warning.real_location_of_greenhouse || "—",
                resolvedLocation: taskData.location_warning.resolved_location || taskData.location_warning.spoken_location || "—",
              })}
            </p>
          </div>
        </div>
      )}

      {/* Hierarchy mismatch banner */}
      {hierarchyMismatch && taskData.location_warning?.type !== "location_conflict" && taskData.location_warning?.type !== "greenhouse_not_in_location" && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-xs text-amber-700">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{hierarchyMismatch}</span>
        </div>
      )}

      {/* Inline-editable rows */}
      <div className="bg-white rounded-2xl border border-[#e9eff6] divide-y divide-[#eef3f8] overflow-hidden">
        {/* Name */}
        <Row
          icon={FileText}
          label={t("voiceModal.fields.name")}
          value={taskData.title}
          isEditing={editingField === "title"}
          onEditOpen={() => openEdit("title")}
          onEditClose={closeEdit}
          editor={
            <Input
              autoFocus
              value={taskData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className={triggerCls}
            />
          }
        />

        {/* Type */}
        <Row
          icon={Tag}
          tone={getTaskTypeIcon(taskData.type).color}
          label={t("voiceModal.fields.type")}
          value={typeLabel}
          isEditing={editingField === "type"}
          onEditOpen={() => openEdit("type")}
          onEditClose={closeEdit}
          editor={
            <Select value={taskData.type} onValueChange={(v) => { update("type", v); closeEdit(); }}>
              <SelectTrigger className={triggerCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="treatment">{t("planner.types.treatment")}</SelectItem>
                <SelectItem value="monitoring">{t("planner.types.monitoring")}</SelectItem>
                <SelectItem value="maintenance">{t("planner.types.maintenance")}</SelectItem>
                <SelectItem value="visit">{t("planner.types.visit")}</SelectItem>
                <SelectItem value="inspection">{t("planner.types.inspection")}</SelectItem>
                <SelectItem value="other">{t("planner.types.other")}</SelectItem>
              </SelectContent>
            </Select>
          }
        />

        {/* Date */}
        <Row
          icon={CalendarIcon}
          tone="#2E84DE"
          label={t("voiceModal.fields.date")}
          value={taskData.scheduled_date}
          isEditing={editingField === "date"}
          onEditOpen={() => openEdit("date")}
          onEditClose={closeEdit}
          editor={
            <Input
              type="date"
              autoFocus
              value={taskData.scheduled_date}
              onChange={(e) => update("scheduled_date", e.target.value)}
              className={triggerCls}
            />
          }
        />

        {/* Time */}
        <Row
          icon={Clock}
          tone="#0FA9B8"
          label={t("voiceModal.fields.time")}
          value={taskData.scheduled_time || "—"}
          isEditing={editingField === "time"}
          onEditOpen={() => openEdit("time")}
          onEditClose={closeEdit}
          editor={
            <Input
              type="time"
              autoFocus
              value={taskData.scheduled_time || "08:00"}
              onChange={(e) => update("scheduled_time", e.target.value)}
              className={triggerCls}
            />
          }
        />

        {/* Location */}
        <Row
          icon={MapPin}
          tone="#16A971"
          label={t("voiceModal.fields.location")}
          value={taskData.location_name || ""}
          isEditing={editingField === "location"}
          onEditOpen={() => openEdit("location")}
          onEditClose={closeEdit}
          editor={
            locationOptions.length > 0 ? (
              <Select
                value={taskData.location_id || undefined}
                onValueChange={(v) => { handleLocationSelect(v); closeEdit(); }}
              >
                <SelectTrigger className={triggerCls}>
                  <SelectValue placeholder={t("voiceModal.fields.location")} />
                </SelectTrigger>
                <SelectContent>
                  {locationOptions.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-slate-500">{t("voiceTask.missingLocations")}</p>
            )
          }
        />

        {/* Greenhouse (required) */}
        <Row
          icon={Home}
          tone="#16A971"
          label={t("voiceModal.fields.greenhouse")}
          value={taskData.greenhouse_name || ""}
          warning={!taskData.greenhouse_id}
          isEditing={editingField === "greenhouse"}
          onEditOpen={() => openEdit("greenhouse")}
          onEditClose={closeEdit}
          editor={
            filteredGreenhouses.length > 0 ? (
              <Select
                value={taskData.greenhouse_id || undefined}
                onValueChange={(v) => { handleGreenhouseSelect(v); closeEdit(); }}
              >
                <SelectTrigger className={triggerCls}>
                  <SelectValue placeholder={t("voiceModal.fields.greenhouse")} />
                </SelectTrigger>
                <SelectContent>
                  {filteredGreenhouses.map(gh => (
                    <SelectItem key={gh.id} value={gh.id}>{gh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-slate-500">{t("voiceTask.missingGreenhouses")}</p>
            )
          }
        />

        {/* Block / Section — multi-select checklist (Select all + toggle each section) */}
        <Row
          icon={Layers}
          tone="#16A971"
          label={t("voiceModal.fields.section")}
          value={sectionDisplay}
          isEditing={editingField === "block"}
          onEditOpen={() => openEdit("block")}
          onEditClose={closeEdit}
          editor={
            filteredBlocks.length > 0 ? (
              <div className="space-y-1 rounded-xl border border-[#e2ecf5] bg-white p-1.5 max-h-60 overflow-y-auto">
                {/* Select all */}
                <button
                  type="button"
                  onClick={handleSelectAllSections}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-semibold transition-colors text-left",
                    allSectionsSelected ? "bg-teal-500 text-white" : "text-slate-700 hover:bg-slate-50"
                  )}
                >
                  <span className={cn(
                    "w-5 h-5 rounded-full flex items-center justify-center shrink-0",
                    allSectionsSelected ? "bg-white/25 text-white" : "border border-[#b6dded] bg-white"
                  )}>
                    {allSectionsSelected && <CheckIcon className="w-3 h-3" strokeWidth={3} />}
                  </span>
                  {t("voiceTask.allSections")}
                </button>
                {filteredBlocks.map(b => {
                  const sel = (taskData.block_ids || []).includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleSection(b.id)}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium text-slate-800 hover:bg-slate-50 transition-colors text-left"
                    >
                      <span className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center shrink-0",
                        sel ? "bg-teal-500 text-white" : "border border-[#b6dded] bg-white"
                      )}>
                        {sel && <CheckIcon className="w-3 h-3" strokeWidth={3} />}
                      </span>
                      {b.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-500">{t("voiceTask.missingBlocks")}</p>
            )
          }
        />

        {/* Treatment mode toggle — configured recipe vs ad-hoc custom products */}
        {taskData.type === "treatment" && (
          <Row
            icon={FlaskConical}
            tone="#6366F1"
            label={t("voiceTask.treatmentMode")}
            editable={false}
            value={
              <div className="flex gap-1.5 mt-0.5">
                {(["configured", "custom"] as const).map((kind) => {
                  const active = kind === "custom" ? isCustom : !isCustom;
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => setTreatmentKind(kind)}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors",
                        active ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-[#e2ecf5] hover:bg-slate-50"
                      )}
                    >
                      {kind === "custom" ? t("voiceTask.customTreatment") : t("voiceTask.configuredTreatment")}
                    </button>
                  );
                })}
              </div>
            }
          />
        )}

        {/* Pest (read-only) — configured treatment tasks only */}
        {taskData.type === "treatment" && !isCustom && (
          <Row
            icon={Bug}
            tone="#E0962E"
            label={t("voiceModal.fields.pest")}
            value={taskData.pest_name ? getPestName({ name: taskData.pest_name, translation_key: taskData.pest_translation_key ?? null }) : ""}
            editable={false}
          />
        )}

        {/* Treatment — configured treatment tasks only */}
        {taskData.type === "treatment" && !isCustom && (
          <Row
            icon={FlaskConical}
            tone={PILL_COLORS[treatmentMode === "biological" ? "green" : "red"]}
            label={t("voiceModal.fields.treatment")}
            value={taskData.treatment_name || ""}
            warning={treatmentMissing}
            isEditing={editingField === "treatment"}
            onEditOpen={() => openEdit("treatment")}
            onEditClose={closeEdit}
            editor={
              loadingTreatments ? (
                <div className="flex items-center gap-2 py-1">
                  <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                </div>
              ) : treatmentOptions.length > 0 ? (
                <Select
                  value={taskData.treatment_id || undefined}
                  onValueChange={(v) => { handleTreatmentSelect(v); closeEdit(); }}
                >
                  <SelectTrigger className={cn(triggerCls, taskData.treatment_id ? "" : "border-amber-300")}>
                    <SelectValue placeholder={t("voiceTask.selectTreatment")} />
                  </SelectTrigger>
                  <SelectContent>
                    {treatmentOptions.map(tr => (
                      <SelectItem key={tr.id} value={tr.id}>{tr.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-slate-500">{t("voiceTask.noTreatmentsForPest")}</p>
              )
            }
          />
        )}

        {/* Worker (required) */}
        <Row
          icon={User}
          avatar={{ name: taskData.worker_name, url: selectedWorkerAvatar }}
          label={t("voiceModal.fields.worker")}
          value={taskData.worker_name || ""}
          warning={!taskData.assigned_to}
          isEditing={editingField === "worker"}
          onEditOpen={() => openEdit("worker")}
          onEditClose={closeEdit}
          editor={
            loadingWorkers ? (
              <div className="flex items-center gap-2 py-1">
                <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
              </div>
            ) : opcionesVisibles.length > 0 ? (
              <Select
                value={taskData.assigned_to || undefined}
                onValueChange={(v) => { handleWorkerSelect(v); closeEdit(); }}
              >
                <SelectTrigger className={triggerCls}>
                  <SelectValue placeholder={t("voiceTask.selectWorker")} />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Primero los asignados a los sectores dictados; el resto, debajo.
                    const assigned = opcionesVisibles.filter(w => blockAssignedIds.has(w.user_id));
                    const others = opcionesVisibles.filter(w => !blockAssignedIds.has(w.user_id));
                    if (assigned.length === 0) {
                      return opcionesVisibles.map(w => (
                        <SelectItem key={w.user_id} value={w.user_id}>{w.full_name}</SelectItem>
                      ));
                    }
                    return (
                      <>
                        <SelectGroup>
                          <SelectLabel>{t("planner.assignedToLocation")}</SelectLabel>
                          {assigned.map(w => (
                            <SelectItem key={w.user_id} value={w.user_id}>{w.full_name}</SelectItem>
                          ))}
                        </SelectGroup>
                        {others.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>{t("planner.othersGroup")}</SelectLabel>
                            {others.map(w => (
                              <SelectItem key={w.user_id} value={w.user_id}>{w.full_name}</SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-slate-500">—</p>
            )
          }
        />

        {/* Priority */}
        <Row
          icon={Flag}
          tone={PILL_COLORS[taskData.priority === "high" ? "red" : taskData.priority === "low" ? "green" : "amber"]}
          label={t("voiceModal.fields.priority")}
          value={priorityLabel}
          isEditing={editingField === "priority"}
          onEditOpen={() => openEdit("priority")}
          onEditClose={closeEdit}
          editor={
            <Select value={taskData.priority} onValueChange={(v) => { update("priority", v); closeEdit(); }}>
              <SelectTrigger className={triggerCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t("planner.priorities.low")}</SelectItem>
                <SelectItem value="medium">{t("planner.priorities.medium")}</SelectItem>
                <SelectItem value="high">{t("planner.priorities.high")}</SelectItem>
              </SelectContent>
            </Select>
          }
        />

        {/* Notes (read-only display, if any) */}
        {taskData.notes && (
          <Row
            icon={CalendarIcon}
            label={t("common.notes")}
            value={taskData.notes}
            editable={false}
          />
        )}
      </div>

      {!taskData.location_id && !taskData.greenhouse_id && (
        <div className="flex items-center gap-2 text-xs text-amber-600">
          <AlertTriangle className="w-3.5 h-3.5" />
          {t("voiceTask.noLocationDetected")}
        </div>
      )}

      {/* Location stock warning for treatment products */}
      {locationWarning && locationWarning.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{t("planner.locationStockWarningTitle")}</span>
          </div>
          {locationWarning.map((p, i) => (
            <div key={i} className="text-xs">
              <span className="text-slate-800 font-medium">{p.product_name}</span>
              <p className="text-slate-500 mt-0.5">
                {p.available_locations.length > 0
                  ? t("planner.productAvailableAt", { locations: p.available_locations.join(", ") })
                  : t("planner.productNoLocation")}
              </p>
            </div>
          ))}
          <p className="text-xs text-slate-500 mt-1">{t("planner.applyAnywayNote")}</p>
        </div>
      )}

      {/* Custom (ad-hoc) treatment builder */}
      {isCustom && (
        <div className="space-y-3">
          {/* Products the manager must disambiguate (spoken name matched 0 or >1 products) */}
          {(taskData.unresolved_products || []).length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3 space-y-2.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>{t("voiceTask.resolveProductsTitle")}</span>
              </div>
              {(taskData.unresolved_products || []).map((u, i) => {
                /* Solo se ofrece lo que `handlePickUnresolved` sabe resolver, o sea lo
                   que sigue estando en el inventario: un candidato cuyo producto ya no
                   existe (borrado entre el dictado y la confirmación) se elegía y no
                   pasaba absolutamente nada, porque el handler salía por `!inv`. */
                const invIds = new Set(inventoryProducts.map((p) => p.id));
                const candidates = u.ambiguous_candidates && u.ambiguous_candidates.length > 0
                  ? u.ambiguous_candidates.filter((c) => invIds.has(c.id)).map((c) => ({ id: c.id, label: c.variant_label ? `${c.name} · ${c.variant_label}` : c.name }))
                  : inventoryProducts.map((p) => ({ id: p.id, label: p.variant_label ? `${p.product_name} · ${p.variant_label}` : p.product_name }));
                return (
                  <div key={i} className="space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs text-slate-700 min-w-0">{t("voiceTask.pickProductFor", { name: u.spoken_name || "?" })}</p>
                      <button
                        type="button"
                        onClick={() => handleDismissUnresolved(i)}
                        aria-label={t("common.dismiss")}
                        title={t("common.dismiss")}
                        className="shrink-0 -mt-1 -mr-1 p-1.5 rounded-lg text-amber-600 hover:text-amber-800 hover:bg-amber-100 transition-colors"
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {/* Con la lista vacía, Radix abría un desplegable de altura cero: el
                        usuario tocaba «Selecciona un producto» y no ocurría nada, sin
                        una sola pista de por qué. Ahora se dice. */}
                    {inventoryLoaded && candidates.length === 0 ? (
                      <p className="text-xs text-amber-700">
                        {inventoryProducts.length === 0
                          ? t("voiceTask.emptyInventoryForPick")
                          : t("voiceTask.productGoneFromInventory")}
                      </p>
                    ) : (
                      <Select onValueChange={(v) => handlePickUnresolved(i, v)} disabled={candidates.length === 0}>
                        <SelectTrigger className={triggerCls}><SelectValue placeholder={t("voiceTask.selectProduct")} /></SelectTrigger>
                        <SelectContent>
                          {candidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <TreatmentProductsSection
            stockFlexible={stockFlexible}
            products={(taskData.resolved_products || []).map(toTreatmentProduct)}
            inventoryProducts={inventoryProducts.map((p) => ({ id: p.id, product_name: p.variant_label ? `${p.product_name} · ${p.variant_label}` : p.product_name, unit: p.unit, quantity: p.quantity }))}
            isCustomizing={isCustomizing}
            onCustomizingChange={setIsCustomizing}
            onProductRateValueChange={handleCustomRateValueChange}
            onProductRateUnitChange={handleCustomRateUnitChange}
            onRemoveProduct={handleRemoveCustomProduct}
            onAddProduct={handleAddCustomProduct}
            numGreenhouses={1}
            totalBlocks={(taskData.block_ids || []).length}
            showAlwaysVisible
            totalAreaHa={customAreaM2 / 10000}
            caldoL={caldoL}
            caldoMissing={caldoMissing}
            waterVolume={taskData.water_volume ?? null}
            waterVolumeUnit={taskData.water_volume_unit || "L"}
            onWaterVolumeChange={handleWaterVolumeChange}
            onWaterVolumeUnitChange={handleWaterVolumeUnitChange}
          />
        </div>
      )}

      {/* Resolved products breakdown — configured treatment recipe only (read-only) */}
      {!isCustom && taskData.resolved_products && taskData.resolved_products.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#e9eff6] p-3 space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
            <Package className="w-3.5 h-3.5" />
            <span className="font-medium">{t("voiceTask.productsFromTreatment")}</span>
          </div>
          {taskData.resolved_products.map((p, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-slate-800 truncate mr-2">{p.product_name}</span>
              <span className="text-primary font-medium whitespace-nowrap">
                {formatStock(p.amount)} {p.unit}
                {p.amount_per_ha != null && (
                  <span className="text-slate-500 text-xs ml-1">
                    ({formatStock(p.amount_per_ha)}/{t("voiceTask.ha")})
                  </span>
                )}
              </span>
            </div>
          ))}
          {/* Caldo volume input for concentration-based recipes (mL/100L, g/100L) */}
          {taskData.resolved_products.some(p => p.rate_unit && classifyUnit(p.rate_unit) === "per_caldo") && (
            <div className="pt-2 mt-1 border-t border-[#e9eff6]">
              <span className="text-xs text-slate-500">{t("sprayLogs.waterVolumeOptional")}</span>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input
                  type="number"
                  min={0}
                  step="0.1"
                  value={taskData.water_volume ?? ""}
                  placeholder={t("sprayLogs.notIndicated")}
                  className="h-9"
                  onChange={(e) => {
                    const value = e.target.value === "" ? null : parseFloat(e.target.value);
                    const unit = taskData.water_volume_unit || "L";
                    const areaM2 = taskData.resolved_area_m2 || 0;
                    const caldoL = resolveCaldoLiters(value, unit, areaM2);
                    const products = (taskData.resolved_products || []).map(p => {
                      if (p.rate_unit && classifyUnit(p.rate_unit) === "per_caldo") {
                        const r = resolveProductAmount({ value: p.rate_value || 0, unit: p.rate_unit, areaM2, caldoL, stockUnit: p.stock_unit || p.unit });
                        return { ...p, amount: r.error ? 0 : r.amount, unit: r.amountUnit };
                      }
                      return p;
                    });
                    onTaskDataChange({ ...touch(taskData, "water"), water_volume: value, water_volume_unit: unit, resolved_products: products });
                  }}
                />
                <Select
                  value={taskData.water_volume_unit || "L"}
                  onValueChange={(unit) => {
                    const value = taskData.water_volume ?? null;
                    const areaM2 = taskData.resolved_area_m2 || 0;
                    const caldoL = resolveCaldoLiters(value, unit, areaM2);
                    const products = (taskData.resolved_products || []).map(p => {
                      if (p.rate_unit && classifyUnit(p.rate_unit) === "per_caldo") {
                        const r = resolveProductAmount({ value: p.rate_value || 0, unit: p.rate_unit, areaM2, caldoL, stockUnit: p.stock_unit || p.unit });
                        return { ...p, amount: r.error ? 0 : r.amount, unit: r.amountUnit };
                      }
                      return p;
                    });
                    onTaskDataChange({ ...touch(taskData, "water"), water_volume_unit: unit, resolved_products: products });
                  }}
                >
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L/ha">L/ha</SelectItem>
                    <SelectItem value="L">L</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
